// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Add "hideLoadingOverlay" code inside PreStyle/globalStyle
 */

import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { xCommonLoadingOverlay } from "@keller/App/Handlers/XNG/Common";
import { CustomAppState } from "@keller/App/Store/Reducers";
import React, { FC, useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isErrorModalOpen: state.context.isErrorModalOpen,
        pages: state.data.pages,
        xLoadingOverlay: state.xCommon.loadingOverlay,
        isSigningIn: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
    };
};

const mapDispatchToProps = {
    xCommonLoadingOverlay,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const hideLoadingOverlay = css`
    #react-app:not(.allow-loadingOverlay) {
        div[data-test-selector="loadingOverlaySpinner"],
        svg[class^="LoadingSpinnerStyle"] {
            visibility: hidden;
            width: 0;
            height: 0;
            position: absolute;
            top: -10000px;
        }
    }
`;

export const zIndexLoadingOverlay = 1500;

export const xngLoadingOverlayStyles = css`
    #xng-loading-overlay {
        width: 100%;
        position: fixed;
        height: 100%;
        z-index: ${zIndexLoadingOverlay};
        top: 0;
        bottom: 0;
        @media print {
            display: none;
        }
    }
`;

const XngLoadingOverlay: FC<Props> = ({
    isErrorModalOpen,
    pages,
    xLoadingOverlay,
    xCommonLoadingOverlay,
    isSigningIn,
}) => {
    const isShowing = () => isSigningIn || (xLoadingOverlay && xLoadingOverlay?.length > 0);
    const [showLoading, setShowLoading] = useState(isShowing());

    useEffect(() => {
        const showXngLoading = isShowing();
        setShowLoading(showXngLoading);

        if (typeof document === "undefined") {
            return;
        }

        /** To hide all loadingOverlay and spinner
         * We added "hideLoadingOverlay" inside PreStyle/globalStyle
         */
        const element = document.getElementById("react-app");
        if (showXngLoading) {
            element?.classList.remove(`allow-loadingOverlay`);
        } else {
            element?.classList.add(`allow-loadingOverlay`);
        }
    }, [isSigningIn, xLoadingOverlay]);

    /** To close LoadingOverlay when the page redirect to another path */
    useEffect(() => {
        xCommonLoadingOverlay({ key: "clear", value: "" });
    }, [pages.location]);

    /** To close LoadingOverlay when ErrorModal open */
    useEffect(() => {
        if (isErrorModalOpen) {
            xCommonLoadingOverlay({ key: "clear", value: "" });
        }
    }, [isErrorModalOpen]);

    const LoadingElement = ({ showLoading }: { showLoading?: boolean }) => (
        <>
            {showLoading && (
                <LoadingOverlay id="xng-loading-overlay" data-test-selector="global-loadingOverlay" loading={true} />
            )}
        </>
    );

    if (IS_SERVER_SIDE) {
        return <LoadingElement showLoading={true} />;
    }

    return ReactDOM.createPortal(<LoadingElement showLoading={showLoading} />, document.body);
};

export default connect(mapStateToProps, mapDispatchToProps)(XngLoadingOverlay);
